import { forwardRef, type ReactElement, type ReactNode } from 'react'
import { Box, keyframes, useTheme } from '@mui/material'
import { purple } from '@mui/material/colors'
import { MaterialSymbol } from '@ui2/icons'
import { useSmartSuggestion } from '@editorHooks'

const suggestionPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(234, 128, 252, 0.18);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(234, 128, 252, 0.12);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(234, 128, 252, 0);
  }
`

const suggestionBorderBreath = keyframes`
  0% {
    border-color: rgba(234, 128, 252, 0.45);
  }
  50% {
    border-color: ${purple.A200};
  }
  100% {
    border-color: rgba(234, 128, 252, 0.45);
  }
`

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sparkleFloat = keyframes`
  0%, 100% {
    transform: translate(34%, -34%) scale(1) rotate(0deg);
  }
  50% {
    transform: translate(34%, -40%) scale(1.12) rotate(8deg);
  }
`

type SmartSuggestionProps = {
  children: ReactNode
  text: string
  onAccept: () => void
  onAbort?: () => void
  enabled: boolean
}

const SmartSuggestion = forwardRef<HTMLSpanElement, SmartSuggestionProps>(
  ({ children, text, onAccept, onAbort, enabled }, ref): ReactElement => {
    const theme = useTheme()
    const suggestionEnabled = useSmartSuggestion({
      type: 'html-suggestion',
      text,
      onAccept,
      onAbort,
      enabled,
    })

    return (
      <Box
        ref={ref}
        component="span"
        sx={{
          position: 'relative',
          display: 'inline-flex',
          borderRadius: 2,
          ...(suggestionEnabled
            ? {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: -2,
                  border: '1px solid rgba(234, 128, 252, 0.45)',
                  borderRadius: 'inherit',
                  pointerEvents: 'none',
                  animation: `${suggestionBorderBreath} 3000ms ease-in-out infinite, ${suggestionPulse} 3000ms ease-in-out infinite`,
                },
              }
            : {}),
        }}
      >
        {children}
        {suggestionEnabled ? (
          <Box
            component="span"
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 1,
              width: 17,
              height: 17,
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.common.white,
              bgcolor: purple.A200,
              boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
              pointerEvents: 'none',
              transform: 'translate(50%, -50%)',
              // animation: `${sparkleFloat} 1800ms ease-in-out infinite`,
            }}
          >
            <MaterialSymbol icon="auto_awesome" size={12} filled />
          </Box>
        ) : null}
      </Box>
    )
  },
)

SmartSuggestion.displayName = 'SmartSuggestion'

export default SmartSuggestion
